<template>
    <v-layout>
        <v-app>
            <v-app-bar border="b" flat height="56">
                <div class="d-flex align-center overflow-x-auto px-2" style="max-width: calc(100% - 68px);">
                    <v-btn v-for="item in menuItems"
                           :key="item.name"
                           :to="{ name: item.name }"
                           class="text-none"
                           :icon="$vuetify.display.xsOnly"
                           :active="$route.name === item.name"
                           :min-width="$vuetify.display.xsOnly ? 40 : undefined"
                           density="compact">
                        <v-icon size="20">{{ item.icon }}</v-icon>
                        <span v-if="$vuetify.display.smAndUp"
                              class="ms-1 text-body-2">
                            {{ $t(item.text) }}
                        </span>
                    </v-btn>
                </div>

                <v-chip v-if="isDemo"
                        class="ms-2 flex-shrink-0"
                        color="primary"
                        size="small"
                        label
                        variant="tonal">
                    DEMO
                </v-chip>

                <v-spacer></v-spacer>

                <v-btn icon
                       density="compact"
                       class="ms-auto"
                       style="min-width: 40px;">
                    <v-menu location="bottom end" :close-on-content-click="true">
                        <template v-slot:activator="{ props }">
                            <v-btn icon v-bind="props" density="compact">
                                <v-icon size="20">mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>

                        <v-list>
                            <v-list-item @click="toggleTheme">
                                <template v-slot:prepend>
                                    <v-icon>mdi-theme-light-dark</v-icon>
                                </template>
                                <v-list-item-title>
                                    {{ $t('switchTheme', { theme: $vuetify.theme.global.current.dark ? 'Light' : 'Dark' }) }}
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="toggleFullscreen">
                                <template v-slot:prepend>
                                    <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                                </template>
                                <v-list-item-title>
                                    {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="state.showDuckDBDialog = true">
                                <template v-slot:prepend>
                                    <v-icon>mdi-duck</v-icon>
                                </template>
                                <v-list-item-title>
                                    DuckDB Query
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-btn>
            </v-app-bar>

            <v-dialog v-model="state.showWarningDialog" persistent max-width="400">
                <v-card>
                    <v-card-title class="text-h5">
                        Missing Parameters
                    </v-card-title>
                    <v-card-text>
                        The following required parameters are missing from the URL:
                        <ul class="mt-2">
                            <li v-for="param in state.missingParams" :key="param" class="mt-1">
                                {{ param }}
                            </li>
                        </ul>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="error" @click="handleMissingParams">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- DuckDB Query Dialog -->
            <DuckDBQueryDialog v-model="state.showDuckDBDialog" />

            <v-main>
                <div class="pa-4 content-container">
                    <ActiveFiltersBar />

                    <router-view></router-view>

                    <WelcomeDialog :api-progress="apiProgress"
                                   :total-api-requests="totalApiRequests"
                                   :table-progress="tableProgress" />
                </div>

                <ReportProgressFab />
            </v-main>
        </v-app>
    </v-layout>
</template>

<script setup>
    import { reactive, computed, watch, onMounted, onUnmounted, ref } from 'vue';
    import { useStore } from 'vuex';
    import { useTheme } from 'vuetify';
    import { proxyUrl } from '/config';
    import WelcomeDialog from './components/dialogs/WelcomeDialog.vue';
    import DuckDBQueryDialog from './components/dialogs/DuckDBQueryDialog.vue';
    import ReportProgressFab from './components/ReportProgressFab.vue';
    import ActiveFiltersBar from './components/ActiveFiltersBar.vue';
    import { loadDemoData } from './mockData/loadDemoData';
    import { fieldTagsQuery } from './utils/assetFields';
    import { finalizeDataLoad } from './utils/assetModel';

    const store = useStore();
    const vuetifyTheme = useTheme();
    const currentTheme = computed(() => store.state.theme);

    const getUserColorScheme = () => {
        if (window.matchMedia) {
            if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                vuetifyTheme.global.name.value = 'dark'; //set immediately
                return 'dark';
            } else {
                return 'light';
            }
        } else {
            return currentTheme.value;
        }
    };

    const theme = ref(getUserColorScheme());
    store.commit('setTheme', theme.value);

    const state = reactive({
        showWarningDialog: false,
        missingParams: [],
        showDuckDBDialog: false,
    });

    const menuItems = computed(() => [
        { name: 'Overview', icon: 'mdi-view-dashboard-outline', text: 'overview' },
        { name: 'Inspection', icon: 'mdi-clipboard-check-outline', text: 'inspectionMaintenance' },
        { name: 'AssetData', icon: 'mdi-database-outline', text: 'assetData' }
    ]);

    const settings = computed(() => store.state.settings);
    const loadDashboard = computed(() => store.getters.getLoadDashboard);

    const buildMode = ref('production');
    const isFullscreen = ref(false);
    const isDemo = ref(false);

    const apiProgress = ref(0);
    const totalApiRequests = ref(0);
    const tableProgress = ref({});

    const requiredParams = [
        'scopedToken',
        'projectName',
        'projectCode',
        'locale',
        'apiUrl'
    ];

    const detectDemoMode = () => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('demo') === 'true') return true;
        return requiredParams.some(param => !urlParams.get(param));
    };

    const parseQuerystring = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const params = {

            scopedToken: urlParams.get('scopedToken'),
            projectName: urlParams.get('projectName'),
            projectCode: urlParams.get('projectCode'),
            locale: urlParams.get('locale'),
            apiUrl: urlParams.get('apiUrl')

        };

        // Validate parameters
        state.missingParams = requiredParams.filter(param => !params[param]);

        if (state.missingParams.length > 0) {
            state.showWarningDialog = true;
            console.error('Missing required parameters:', state.missingParams);
            return false;
        }

        // If all parameters are present, store them
        store.commit('setValues', {
            ...params
        });

        // Split the token into parts
        const payloadB64 = params.scopedToken.split('.')[1];

        // Decode the payload
        const payloadJson = base64UrlDecode(payloadB64);
        let payload;
        try {
            payload = JSON.parse(payloadJson);
        } catch (parseError) {
            console.error('Failed to parse JWT payload:', parseError);
            throw new Error('Invalid JWT token payload');
        }
        console.log("User ID:", payload.rdrive_user_id);
        await store.dispatch('updateUserId', payload.rdrive_user_id);

        return true;
    };

    const handleMissingParams = () => {
        state.showWarningDialog = false;
        window.close();
    };

    function base64UrlDecode(input) {
        // Replace URL-safe characters and add padding if needed
        input = input.replace(/-/g, '+').replace(/_/g, '/');
        const pad = input.length % 4;
        if (pad) {
            input += '='.repeat(4 - pad);
        }

        // Decode the base64 string to binary
        const binaryString = atob(input);
        // Convert to Uint8Array
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        // Decode to UTF-8 string
        return new TextDecoder().decode(bytes);
    }

    const getData = async () => {
        try {
            if (!store.getters.isDuckDBInitialized) {
                console.error('DuckDB not initialized yet. Please wait for initialization to complete.');
                return;
            }

            apiProgress.value = 0;
            store.commit('setDataLoaded', false);

            const duckDBService = store.getters.getDuckDBService;
            const conn = store.getters.getDBConnection;

            if (!conn) {
                throw new Error('DuckDB connection not initialized');
            }

            // Create item_templates table with the correct schema if it doesn't exist
            await conn.query(`
      CREATE TABLE IF NOT EXISTS item_templates (
        Id VARCHAR,
        Title VARCHAR,
        Code VARCHAR,
        CategoryTitle VARCHAR,
        SubcategoryTitle VARCHAR,
        Type VARCHAR
      );
    `);

            // [ASSUMPTION] Assets are served by the same tag-pivot route family
            // as issues, with a BI-Asset template tag. The field tags come from
            // the registry in src/utils/assetFields.js - add a tag there, not
            // here. Verify the route segment ('issues' may be a fixed route
            // name or need to become 'assets') against the real API.
            const apiRequests = [
                { path: `/api/${settings.value.projectCode}/reporting/issues/tag-pivot-as-parquet/BI-Asset?${fieldTagsQuery()}`, table: 'assets', responseType: 'parquet', requiresPaging: true },
                // Created Date and closed-date come from here: the pivot carries
                // no creation timestamp. Optional - the model degrades to
                // LastUpdated if this request fails.
                { path: `/api/${settings.value.projectCode}/reporting/items/status-history-short?templateTags=BI-Asset`, table: 'status_history', responseType: 'parquet' },
                { path: `/api/${settings.value.projectCode}/reporting/project-item-templates`, table: 'item_templates', responseType: 'parquet' },
                { path: '/api/categories', table: 'categories', responseType: 'parquet' },
                { path: `/api/${settings.value.projectCode}/reporting/items/item-status-templates`, table: 'status_templates', responseType: 'parquet' },
                { path: `/api/${settings.value.projectCode}/drawings`, table: 'drawings', responseType: 'parquet' },
                { path: `/api/${settings.value.projectCode}/locations`, table: 'locations', responseType: 'parquet' },
                { path: `/api/${settings.value.projectCode}/teams`, table: 'teams', responseType: 'parquet' },
                { path: `/api/${settings.value.projectCode}/bi/teams`, table: 'team_users', responseType: 'parquet' },
                { path: `/api/${settings.value.projectCode}/users`, table: 'users', responseType: 'parquet' },
                { path: '/api/users/current', table: 'current_user', responseType: 'json' },
                { path: `/api/${settings.value.projectCode}/users/current/permission-rules`, table: 'permission_rules', responseType: 'json' },
                { path: '/api/company', table: 'company', responseType: 'json' },
            ];
            totalApiRequests.value = apiRequests.length;

            // Progress callback function
            const progressCallback = (progress) => {
                tableProgress.value = { ...progress, timestamp: Date.now() };
            };

            const results = await Promise.allSettled(
                apiRequests.map(async ({ path, table, responseType, requiresPaging = false }) => {
                    try {
                        switch (responseType) {
                            case 'parquet':
                                console.log(`[${new Date().toISOString()}] Fetching parquet for ${table} from: ${path}`);

                                if (requiresPaging) {
                                    await duckDBService.downloadAndInsertParquetWithPaging(path, table, progressCallback);
                                } else {
                                    await duckDBService.downloadAndInsertParquet(path, table, progressCallback);
                                }
                                break;
                            case 'arrow':
                                await duckDBService.downloadAndInsertArrow(path, table, progressCallback);
                                break;
                            default: // JSON or any other type
                                await duckDBService.downloadAndInsertJSON(path, table, progressCallback);
                                break;
                        }
                        apiProgress.value += 1;
                        return { table };
                    } catch (error) {
                        console.error(`[${new Date().toISOString()}] Error processing ${table} from ${path}:`, error);
                        throw error; // Re-throw to capture in Promise.allSettled
                    }
                })
            );

            await conn.loadData(`
      UPDATE status_templates
      SET sort =
          CASE
              WHEN isTerminal AND NOT isPassed THEN 50
              ELSE sort +
                  CASE WHEN isDraft THEN -100 ELSE 0 END +
                  CASE WHEN isInitial THEN -50 ELSE 0 END +
                  CASE WHEN isTerminal THEN 100 ELSE 0 END
          END;
    `);

            const failedResults = results.filter(result => result.status === 'rejected');
            if (failedResults.length > 0) {
                console.error(`[${new Date().toISOString()}] Some API requests failed:`, failedResults);
            }

            // Builds asset_items / unique_assets, seeds the cross-filter and
            // flips dataLoaded once the model is queryable.
            await finalizeDataLoad(store);

            store.commit('setShowWelcomeDialog', false);
            store.commit('setLoadDashboard', false);

            // Query DuckDB for logoUrl from 'company' table after data is loaded
            try {
                const sql = 'SELECT logoUrl FROM company LIMIT 1;';
                const result = await conn.query(sql);
                const companyData = result.toArray();

                if (companyData && companyData.length > 0 && companyData[0].logoUrl) {
                    const logoUrl = companyData[0].logoUrl;
                    await store.dispatch('updateLogoUrl', logoUrl);
                } else {
                    console.warn('No data found in company table for logoUrl');
                }
            } catch (queryError) {
                console.error('Error querying company table for logoUrl:', queryError);
            }

            // Fetch and populate status templates in store (only statuses used by assets)
            try {
                const statusResult = await conn.query(`
                    SELECT DISTINCT st.*
                    FROM status_templates st
                    INNER JOIN asset_items ai ON st.Id = ai.statusId
                    ORDER BY st.Sort
                `);
                const statusArray = statusResult.toArray();
                const statusTemplatesObj = {};
                statusArray.forEach(status => {
                    statusTemplatesObj[status.Id] = status;
                });
                store.commit('setStatusTemplatesObj', statusTemplatesObj);
            } catch (statusError) {
                console.error('Error fetching status templates:', statusError);
            }
        } catch (error) {
            console.error(`[${new Date().toISOString()}] Error fetching data:`, error);
        }
    };

    const toggleTheme = () => {
        theme.value = theme.value === 'dark' ? 'light' : 'dark';
        store.commit('setTheme', theme.value);
        vuetifyTheme.global.name.value = theme.value;
    };

    const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    colorSchemeQuery.addEventListener('change', (e) => {
        theme.value = e.matches ? 'dark' : 'light';
        vuetifyTheme.global.name.value = theme.value;
    });

    watch(
        () => [
            settings.value.scopedToken,
            settings.value.projectCode,
            loadDashboard.value
        ],
        ([newScopedToken, newProjectCode, newLoadDashboard]) => {
            if (newScopedToken && newProjectCode && newLoadDashboard) {
                getData();
            }
        }
    );

    const updateTheme = (e) => {
        theme.value = e.matches ? 'dark' : 'light';
        store.commit('setTheme', theme.value);
    };

    const toggleFullscreen = async () => {
        try {
            if (!document.fullscreenElement) {
                // Enter fullscreen
                if (document.documentElement.requestFullscreen) {
                    await document.documentElement.requestFullscreen();
                } else if (document.documentElement.webkitRequestFullscreen) { // Safari
                    await document.documentElement.webkitRequestFullscreen();
                } else if (document.documentElement.msRequestFullscreen) { // IE11
                    await document.documentElement.msRequestFullscreen();
                }
                isFullscreen.value = true;
            } else {
                // Exit fullscreen
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (document.webkitExitFullscreen) { // Safari
                    await document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) { // IE11
                    await document.msExitFullscreen();
                }
                isFullscreen.value = false;
            }
        } catch (err) {
            console.error('Error toggling fullscreen:', err);
        }
    };

    const registerEnvironmentListeners = () => {
        store.dispatch('updateBuildMode', buildMode.value);
        store.commit('setTheme', theme.value);
        colorSchemeQuery.addEventListener('change', updateTheme);

        // Add fullscreen change event listeners
        document.addEventListener('fullscreenchange', () => {
            isFullscreen.value = !!document.fullscreenElement;
        });
        document.addEventListener('webkitfullscreenchange', () => {
            isFullscreen.value = !!document.webkitFullscreenElement;
        });
        document.addEventListener('mozfullscreenchange', () => {
            isFullscreen.value = !!document.mozFullscreenElement;
        });
        document.addEventListener('MSFullscreenChange', () => {
            isFullscreen.value = !!document.msFullscreenElement;
        });
    };

    onMounted(async () => {
        buildMode.value = 'webapp';

        if (detectDemoMode()) {
            isDemo.value = true;
            store.commit('setProjectName', 'Demo Property Portfolio');
            store.commit('setProjectCode', 'DEMO');
            store.commit('setLocale', 'en');
            store.commit('setShowWelcomeDialog', false);

            await store.dispatch('initializeDuckDB', { proxyUrl, apiUrl: '', scopedToken: '' });
            registerEnvironmentListeners();
            await loadDemoData(store);
            return;
        }

        const queryParamsValid = await parseQuerystring();
        if (!queryParamsValid) {
            return; // Stop further initialization if parameters are invalid
        }

        const { apiUrl, scopedToken } = store.state.settings;

        // Initialize DuckDB with proxyUrl, apiUrl, and scopedToken
        await store.dispatch('initializeDuckDB', { proxyUrl, apiUrl, scopedToken });

        registerEnvironmentListeners();
    });

    onUnmounted(() => {
        colorSchemeQuery.removeEventListener('change', updateTheme);

        // Remove fullscreen change event listeners
        document.removeEventListener('fullscreenchange', () => {
            isFullscreen.value = !!document.fullscreenElement;
        });
        document.removeEventListener('webkitfullscreenchange', () => {
            isFullscreen.value = !!document.webkitFullscreenElement;
        });
        document.removeEventListener('mozfullscreenchange', () => {
            isFullscreen.value = !!document.mozFullscreenElement;
        });
        document.removeEventListener('MSFullscreenChange', () => {
            isFullscreen.value = !!document.msFullscreenElement;
        });
    });
</script>

<style>
    /* The app shell is pinned to the viewport. Without this a wide visual (the
       Gantt timeline, or an ECharts canvas that measured itself while the page
       was momentarily wide) grows its column, then the page, and the canvas
       re-measures against the new width - a loop that leaves every tab
       horizontally scrolled and the data table off screen. */
    .v-application,
    .v-application__wrap,
    .v-main {
        min-width: 0;
        max-width: 100%;
    }

    .content-container {
        padding: 16px;
        min-width: 0;
        max-width: 100%;
        overflow-x: auto;
    }

    /* Grid columns are flex items, which default to min-width:auto and so can
       never shrink below their content's intrinsic width. */
    .content-container .v-col,
    .content-container [class^="v-col-"],
    .content-container [class*=" v-col-"] {
        min-width: 0;
    }

    /* A chart that is briefly too wide is clipped by its card rather than
       pushing the layout; ECharts then resizes down into the container. */
    .content-container .v-card {
        max-width: 100%;
    }

    .logo-btn {
        padding: 0 0 0 10px !important;
        min-width: 36px !important;
        width: 80px !important;
    }

    .logo-img {
        max-width: 80px;
        object-fit: contain;
    }

    .v-card {
        margin: 0 auto;
    }

    .margin-bottom {
        margin-bottom: 16px;
    }

    .v-chip {
        padding: 0 4px !important;
    }

    .transparent-bg {
        background-color: transparent !important;
    }
</style>
